// Lib imports
import React from 'react';
import { Dropdown } from '@shared/ui-react/Dropdown'
import { Button } from '@shared/ui-react/Button';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';

// Local imports
import { Measurement } from '../../../../../../typings/reality-capture/volumes/models';
import { MapToolsStore } from '@openlayers/MapToolsStore';
import { measurementStore } from '@shared/openlayers/tools/volume/utils/store/measurement-store'
import { MeasurementData } from './measurement-data';
import { MeasurementEditor } from '../../../../map-measurements/MeasurementEditComponent';
import { openChartWindow } from '../chartjs/chart-dialog';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export type MeasurementContainerProps = {
    measurements: Measurement[];
    measurement?: Measurement | null;
    measurementToolIndex: number;
    toolsStore: MapToolsStore;
    isSelectionOpen?: boolean;
    onToggleSelection?: () => void;
}

export const MeasurementContainer = ({ measurements, measurement, measurementToolIndex, toolsStore, isSelectionOpen, onToggleSelection }: MeasurementContainerProps) => {

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (file) {
            try {
                toolsStore.volumeTool.generateDrawingFromKml(file)
            } catch (error) {
                console.error('Error reading KML file:', error)
            }
        }
    }

    const isVisible = measurement && measurementStore.isVolumeVisible(measurement.uuid);

    return (
        <div className="flex flex-col w-full">
            {/* Header Row */}
            <div className="flex flex-col gap-2 mb-4">
                <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-gray-800 tracking-tight text-sm">
                        {measurementToolIndex === 0 ? '2' : measurementToolIndex === 1 ? '3' : '5'}. {__('js_map_measuring_tools.select_measurement')}:
                    </span>
                    {onToggleSelection && (
                        <button
                            className="p-1 text-ik-text-weak hover:text-ik-text-default transition-colors cursor-pointer"
                            onClick={onToggleSelection}
                            title={__('js_map_measuring_tools.surface_selection')}
                        >
                            <i className={`iki iki-chevron-${isSelectionOpen ? 'up' : 'down'} text-sm`} />
                        </button>
                    )}
                </div>

                {/* Measurement Dropdown */}
                <div className="flex flex-wrap items-center">
                    <Dropdown className="bg-white border border-gray-200 min-w-[160px] rounded-md text-gray-800 px-3 py-1.5 transition-colors hover:bg-gray-50 text-sm flex items-center shadow-sm" closeOnBodyClick styledBody={false}>
                        <span className="flex items-center justify-between cursor-pointer w-full">
                            <span className="truncate max-w-[180px] font-medium">
                                {isVisible
                                    ? measurement.name || measurement.id
                                    : __('js_map_measuring_tools.selected_measurement')}
                            </span>
                            <i className="iconf-caret ml-2 text-gray-400" />
                        </span>
                        <ul className="dropdown-list clickable bg-white border border-gray-200 mt-1 rounded-md shadow-lg text-sm z-50">
                            {measurements.length > 0 ? (
                                measurements.filter(m => measurementStore.isVolumeVisible(m.uuid)).map((m) => (
                                    <li key={m.uuid} onClick={() => measurementStore.selectMeasurement(m.uuid)} className="px-3 py-2 hover:bg-gray-50 cursor-pointer truncate max-w-[200px] text-gray-700 transition-colors">
                                        {m.name || m.id}
                                    </li>
                                ))
                            ) : (
                                <li className="px-3 py-2 text-gray-400 italic">{__('js_map_measuring_tools.no_measurements_available')}</li>
                            )}
                        </ul>
                    </Dropdown>

                    {/* Upload KML */}
                    <input
                        type="file"
                        accept=".kml"
                        onChange={handleFileUpload}
                        id="file-upload"
                        className="hidden"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer flex items-center text-gray-700 hover:text-orange-500 text-sm font-medium px-3 py-1.5">
                        <i className="iki iki-upload text-sm"></i>
                        <span>{__('js_map_measuring_tools.upload_kml')}</span>
                    </label>

                    {/* Save and Plot buttons */}
                    {isVisible && (
                        <>
                            <Button 
                                className="ik-button-primary m-1"
                                onClick={() => MeasurementEditor.openSaveVolume(measurement)}
                            >
                                <i className="pi pi-save text-sm"></i>
                                {__('general.save')}
                            </Button>
                            <Button
                                className="ik-button-primary m-1"
                                onClick={() => openChartWindow(measurement)}
                            >
                                <i className="pi pi-chart-bar text-sm"></i>
                                {__('js_map_measuring_tools.plot_volume')}
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Content Body */}
            {isVisible && (
                <div className="w-full">
                    <MeasurementData
                        measurement={measurement}
                        measurements={measurements}
                        toolsStore={toolsStore}
                    />
                </div>
            )}
        </div>
    );
};
